// OTEL
import { EnumObjects, makeBooleanDataField, makeStringDataField } from "@microsoft/oteljs";
import { OneDSEndpoint } from "@microsoft/oteljs-1ds";
import { PrivacyGuard } from "../PrivacyGuard";
import { createOneDSSink } from "./UTelUtilities";
// Constants
import { TenantRegion } from "@ms/uno-constants/lib/local/AppConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { UsageCoreActions } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { OneDSEnvironment } from "@ms/uno-constants/lib/local/LoggingConstants";
import { getClientAppId, setPersistentDataField } from "../LoggingHelper";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
/** Initialize the engagement logger
 * @param getCurrentPageMethod Method that returns the current page
 * @param engagementLogger Engagement logger instance
 * @param appName Application name
 */ export function initializeEngagementLogger(getCurrentPageMethod, engagementLogger, appName) {
    engagementLogger.initializeEngagementLoggerData(appName, AppContext.appConfiguration.sessionMetaData.appMetadata.clientType, getCurrentPageMethod, UsageCoreActions);
}
/**
 * Reset log handlers
 * @param telemetryContext Telemetry Context
 * @param privacyGuardContext Privacy Guard Context
 * @param logHandler Log handler to be reset
 * @param configProvider Config provider
 * @param appName Name of the host application (for logging purposes)
 */ export function resetLogHandler(telemetryContext, privacyGuardContext, logHandler, configProvider, appName) {
    const oneDSInitOptions = getOneDSInitOptions(telemetryContext, configProvider, appName);
    // create a PrivacyGuardInstance
    const privacyGuard = new PrivacyGuard(configProvider().settings.enablePrivacyGuard, privacyGuardContext.userName, privacyGuardContext.userAlias);
    if (oneDSInitOptions) {
        const telemetrySink = createOneDSSink(oneDSInitOptions, privacyGuard);
        if (telemetrySink) {
            const utelInitOption = getUTelLoggingOptions(telemetryContext);
            logHandler.initialize(telemetrySink, utelInitOption.initOptions, utelInitOption.enableConsoleLogging, ()=>{
                telemetrySink.flush(true);
            });
        }
    }
}
/**
 * Get UTel logging options
 * @param telemetryContext Telemetry Context
 */ function getUTelLoggingOptions(telemetryContext) {
    const enableConsoleLogging = AppContext.isPreProductionEnvironment();
    const initOptions = {
        persistentDataFields: getDefaultPersistentDataFields(telemetryContext)
    };
    return {
        initOptions,
        enableConsoleLogging
    };
}
/**
 * Get default persistent data fields for UTel
 * @param telemetryContext Telemetry context for logging
 */ function getDefaultPersistentDataFields(telemetryContext) {
    const { settings, sessionMetaData } = AppContext.appConfiguration;
    const persistentDataFields = [
        makeStringDataField("AppInfo.ExperimentIds", settings.ConfigIDs),
        makeStringDataField("BuildIdentifier", sessionMetaData.clientBuild),
        makeStringDataField("ClientAppId", getClientAppId(sessionMetaData.appMetadata.clientType, telemetryContext.platform)),
        makeStringDataField("RolloutStage", settings.rolloutStage),
        makeBooleanDataField("IsGuestUser", telemetryContext.isGuestUser),
        makeBooleanDataField("IsMsa", telemetryContext.isMSA),
        makeStringDataField("ClientFlavor", sessionMetaData.appMetadata.clientFlavor),
        makeStringDataField("ClientType", sessionMetaData.appMetadata.clientType),
        makeBooleanDataField("IsOnline", navigator.onLine),
        makeStringDataField("Environment", sessionMetaData.environment),
        makeStringDataField("ServerSessionId", sessionMetaData.serverSessionId ?? "Unknown"),
        makeStringDataField("VisibilityState", document.visibilityState)
    ];
    telemetryContext.ringId && persistentDataFields.push(makeStringDataField("RingId", telemetryContext.ringId));
    return persistentDataFields;
}
/**
 * Create name for logging purposes that includes the api name and the call
 * @param apiName Name of the service api
 * @param methodName Name of the method on the service api
 * */ export function createQosEventName(apiName, methodName) {
    return `api.${apiName}.${methodName}`;
}
/**
 * Updates the current subRouteType and subRoute on the logging context
 * @param subRouteType Current subRouteType to log
 * @param subRoute Current subRoute to log
 * @param logHandler Log handler to set persistent data field
 */ export function setCurrentRouteInfoForLogs(subRouteType, subRoute, logHandler) {
    setPersistentDataField("SubRouteType", subRouteType, makeStringDataField, logHandler);
    setPersistentDataField("SubRoute", subRoute.join("/"), makeStringDataField, logHandler);
}
/**
 * Updates the online status
 * @param isOnline Online status
 * @param logHandler Log handler to set persistent data field
 */ export function setIsOnlineStatusForLogs(isOnline, logHandler) {
    setPersistentDataField("IsOnline", isOnline, makeBooleanDataField, logHandler);
}
/**
 * Get the log string from the error object
 * @param error Error object
 */ export function generateLogStringFromError(error) {
    // check if error is an Error object
    if (error instanceof Error) {
        return `[ErrorType=${error.name}][Exception=${ErrorUtilities.getMessage(error)}]`;
    }
    // check if error is an error object from the ajax request
    if (error?.response || error?.error) {
        let extraTelemetryDataString = "";
        if (error?.extraTelemetryData) {
            Object.entries(error?.extraTelemetryData).forEach(([key, value])=>{
                extraTelemetryDataString += `[${key}=${value}]`;
            });
        }
        return `[StatusCode=${error?.response?.status}][StatusText=${error?.response?.statusText}][ErrorType=${error?.error?.name}]${extraTelemetryDataString}`;
    }
    // get error message for other error types
    return `[Exception=${ErrorUtilities.getMessage(error)}]`;
}
/**
 * Type guard for a failed ajax request result. Verifies the value has the shape produced by
 * IAjaxClient (a `response` field that is a `Response` instance or null), not just any object
 * carrying a `response` property — which can produce mis-typed casts for unrelated thrown objects.
 */ export function isFailedRequestResult(value) {
    if (value == null || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    return candidate.response === null || candidate.response instanceof Response;
}
/**
 * Serialize an unknown thrown value to a string suitable for an Error message.
 * Prefers JSON to avoid the `[object Object]` collapse from `String(value)`.
 */ export function stringifyUnknownError(value) {
    if (typeof value === "string") return value;
    try {
        return JSON.stringify(value) ?? String(value);
    } catch  {
        return String(value);
    }
}
//#region Common Utilities - Used by both App and Service Worker
/**
 * Common telemetry utilities that are shared between the main application and service workers.
 * These functions accept ITelemetryConfigProvider to ensure compatibility with both contexts while
 * maintaining type safety and minimizing the configuration requirements.
 */ /**
 * Get OneDS init options
 * @param telemetryContext Telemetry Context
 * @param configProvider Config provider (supports both IConfigProvider and ITelemetryConfigProvider)
 * @param appName - The name of the application to be displayed in telemetry
 */ function getOneDSInitOptions(telemetryContext, configProvider, appName) {
    const audienceGroup = configProvider().settings.audienceGroup;
    // don't log without audience group
    if (audienceGroup == null) {
        return null;
    }
    const initOptions = {
        appLanguage: telemetryContext.locale,
        appName: appName,
        appVersion: configProvider().sessionMetaData.clientBuild,
        appPlatform: telemetryContext.platform,
        sessionId: configProvider().sessionMetaData.sessionId,
        user: getUserInfoForTelemetry(telemetryContext, configProvider),
        release: {
            audienceGroup: audienceGroup
        }
    };
    return initOptions;
}
/**
 * Get user info (Office.System.User contract) for telemetry
 * If isAnonymous is false, then PrimaryIdentityHash and PrimaryIdentitySpace must be set
 * If PrimaryIdentitySpace is one of OrgIdPUID, OrgIdCID, or UserObjectId, then TenantId must be set
 * If isAnonymous is true, then either PrimaryIdentityHash must be unset or
 * PrimaryIdentityHash must be an anonymously constructed value and PrimaryIdentitySpace must be 'Anonymous'
 * @param telemetryContext Telemetry Context
 * @param configProvider Config provider (supports both IConfigProvider and ITelemetryConfigProvider)
 */ function getUserInfoForTelemetry(telemetryContext, configProvider) {
    const oneDSEndpoint = getOneDSEndpoint(configProvider);
    const isAuthenticated = telemetryContext.userId && telemetryContext.userId !== "";
    const idType = telemetryContext.isMSA ? EnumObjects.PrimaryIdentitySpace.MSAPUID : EnumObjects.PrimaryIdentitySpace.UserObjectId;
    // StrictContractAnonymousUser
    let user = {
        isAnonymous: true,
        environment: oneDSEndpoint
    };
    if (isAuthenticated) {
        if (telemetryContext.tenantId) {
            // StrictContractKnownUser
            user = {
                isAnonymous: false,
                primaryIdentityHash: telemetryContext.userId,
                primaryIdentitySpace: idType,
                tenantId: telemetryContext.tenantId,
                environment: oneDSEndpoint
            };
        } else if (idType === EnumObjects.PrimaryIdentitySpace.MSAPUID) {
            // StrictContractKnownUserOther
            user = {
                isAnonymous: false,
                primaryIdentityHash: telemetryContext.userId,
                primaryIdentitySpace: idType,
                environment: oneDSEndpoint
            };
        }
    }
    return user;
}
/**
 * Get 1DS telemetry endpoint (e.g PUBLIC, EUDB)
 * @param configProvider Config provider (supports both IConfigProvider and ITelemetryConfigProvider)
 */ export function getOneDSEndpoint(configProvider) {
    const oneDSEnvironment = configProvider().settings.oneDSSinkEnvironment;
    switch(oneDSEnvironment){
        case OneDSEnvironment.DoD:
            return OneDSEndpoint.USGOV_DOD;
        case OneDSEnvironment.DoJ:
            return OneDSEndpoint.USGOV_DOJ;
        default:
            return shouldLogToEU(configProvider) ? OneDSEndpoint.EUDB : OneDSEndpoint.PUBLIC;
    }
}
/**
 * Check if logs should be sent to EU
 * @param configProvider Config provider (supports both IConfigProvider and ITelemetryConfigProvider)
 */ function shouldLogToEU(configProvider) {
    const tenantRegion = configProvider().sessionMetaData.tenantRegion;
    const forceLogsToEU = configProvider().settings.forceLogsToEU;
    return forceLogsToEU || tenantRegion === TenantRegion.Eu;
}
/**
 * Create a new OneDSSink
 * @param telemetryContext Telemetry Context
 * @param configProvider Config provider (supports both IConfigProvider and ITelemetryConfigProvider)
 * @param privacyGuardContext Privacy Guard Context
 * @param appName Name of the host application (for logging purposes)
 */ export function createNewOneDSSink(telemetryContext, configProvider, privacyGuardContext, appName) {
    const oneDSInitOptions = getOneDSInitOptions(telemetryContext, configProvider, appName);
    // create a PrivacyGuardInstance
    const privacyGuard = new PrivacyGuard(configProvider().settings.enablePrivacyGuard, privacyGuardContext?.userName, privacyGuardContext?.userAlias);
    return oneDSInitOptions ? createOneDSSink(oneDSInitOptions, privacyGuard) : null;
} //#endregion
